import React from 'react';
import ChangePasswordForm from './ChangePasswordForm';
import { reportError } from 'lib/errors';
import {
  isValidPassword,
  notTooGuessable,
  changePassword,
  ChangePasswordInput,
  User,
} from '@elevator/ui';
import { Formik, FormikHelpers } from 'formik';
import { object as yupObject, string } from 'yup';
import { useSnackbar } from 'notistack';

/**
 * ChangePasswordForm Index Component
 *
 * Container component that wraps the ChangePasswordForm with Formik for form
 * state management and validation. Handles password change submission and
 * integrates with authentication API.
 *
 * Features:
 * - Wraps ChangePasswordForm component in Formik provider.
 * - Validates password fields using Yup schema with custom password strength tests.
 * - Submits password change via authentication API.
 * - Displays snackbar notifications for success/error states.
 *
 * Used in:
 * - `AccountSettings` component for password change modal.
 *
 * @param props - Component props
 * @param props.togglePasswordModalOpen - Callback function to close the password change modal.
 * @param props.currentUser - The currently logged-in user object.
 * @returns ChangePasswordForm wrapped in Formik provider
 */
const Index = (props: ChangePasswordFormIndexProps): React.JSX.Element => {
  const { currentUser, togglePasswordModalOpen } = props;

  const { enqueueSnackbar } = useSnackbar();

  /**
   * Yup validation schema for password change form
   *
   * Validates:
   * - Old password is required
   * - New password meets strength requirements and is not too guessable
   * - Confirm password matches new password
   */
  const validationSchema = yupObject({
    oldPassword: string().required('This field is required'),
    newPassword: string()
      .required('This field is required')
      .test('requirements', 'Password requirements not met', (value) => {
        if (!value) return false;
        return isValidPassword(value);
      })
      .test('notTooGuessable', 'Password too guessable', (value) => {
        if (!value) return false;
        return notTooGuessable(value);
      }),
    confirmPassword: string()
      .test('passwordsMatch', 'Password does not match', function (value) {
        if (!value) return false;
        return value === this.parent.newPassword;
      })
      .required('This field is required'),
  });

  const initialPasswordValues: ChangePasswordInput = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  /**
   * Handles password change form submission
   *
   * Submits password change request to authentication API and handles
   * success/error states with snackbar notifications.
   *
   * @param values - Form values containing old and new passwords
   * @param actions - Formik helpers for form state management
   */
  const handleChangePasswordSubmit = async (
    values: ChangePasswordInput,
    actions: FormikHelpers<ChangePasswordInput>,
  ): Promise<void> => {
    const authEndpoint = process.env.AUTH_ENDPOINT;

    if (!authEndpoint) {
      reportError(new Error('AUTH_ENDPOINT environment variable is not set'), {
        metaData: {
          operation: 'cognito changePassword',
          page: 'Change Password Form',
        },
        user: {
          username: currentUser.username,
          email: currentUser.email,
        },
      });
      enqueueSnackbar('Error: Authentication endpoint not configured', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      actions.setSubmitting(false);
      return;
    }

    try {
      await changePassword(values.oldPassword, values.newPassword, authEndpoint);
      actions.setSubmitting(false);
      actions.resetForm();

      enqueueSnackbar(`Password Changed`, {
        variant: 'info',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });

      togglePasswordModalOpen();
    } catch (err) {
      reportError(err, {
        metaData: {
          operation: 'cognito changePassword',
        },
        user: {
          username: currentUser.username,
          email: currentUser.email,
        },
      });
      enqueueSnackbar(`Error Setting New Password`, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      actions.setSubmitting(false);
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialPasswordValues}
      onSubmit={handleChangePasswordSubmit}
    >
      <ChangePasswordForm togglePasswordModalOpen={togglePasswordModalOpen} {...props} />
    </Formik>
  );
};

export interface ChangePasswordFormIndexProps {
  togglePasswordModalOpen: () => void;
  currentUser: User;
}

export default Index;
